// The brand and deployment config for web apps (brain/engineering/docs/white-label-and-i18n.md).
// Build time: the default from brand.json is bundled (no flash, no request). Run time: a server may put
// <script type="application/json" id="dugzi-brand">{…}</script> in the page to override any part (another
// deployment's domains, a white-label name); it is read once, before the first render.
import { BRAND_DEFAULT } from './brand.generated';
import type { BrandConfig } from './types';

export type { BrandConfig, BrandProduct } from './types';

type Override = Partial<Omit<BrandConfig, 'products'>> & { products?: Record<string, Partial<BrandConfig['products'][string]>> };

/** Merge an override into a config: nested objects field by field, so a deployment changes only what it names. */
export function mergeBrand(base: BrandConfig, o: Override | null | undefined): BrandConfig {
  if (!o || typeof o !== 'object') return base;
  const products: BrandConfig['products'] = { ...base.products };
  for (const [id, p] of Object.entries(o.products ?? {})) {
    const cur = products[id];
    if (cur) products[id] = { ...cur, ...p };
    else if (p.name && p.host) products[id] = { name: p.name, host: p.host };
  }
  return {
    ...base,
    ...(typeof o.name === 'string' && o.name.trim() ? { name: o.name.trim() } : {}),
    legal: { ...base.legal, ...o.legal },
    domains: { ...base.domains, ...o.domains },
    addresses: { ...base.addresses, ...o.addresses },
    assets: { ...base.assets, ...o.assets },
    products,
  };
}

function fromPage(): Override | null {
  try {
    const el = typeof document === 'undefined' ? null : document.getElementById('dugzi-brand');
    return el?.textContent ? (JSON.parse(el.textContent) as Override) : null;
  } catch {
    return null;   // a broken override never breaks the app: the bundled default stands
  }
}

let current: BrandConfig = mergeBrand(BRAND_DEFAULT, fromPage());

/** The active brand config. */
export function brand(): BrandConfig {
  return current;
}

/** Replace parts of the active config (tests, and a deployment that loads its override late). */
export function setBrand(o: Override | null): void {
  current = mergeBrand(BRAND_DEFAULT, o);
}

/** The brand name as written in text ("Dugzi"). Never translated. */
export function brandName(): string {
  return current.name;
}

/** A product's English source name with the brand filled in ("Ask Dugzi"); screens translate it via the catalogue. */
export function productName(id: string): string {
  const p = current.products[id];
  return (p?.name ?? id).replaceAll('{brand}', current.name);
}

/** A product's full name in running text ("Dugzi Tasks"). */
export function productFullName(id: string): string {
  const n = productName(id);
  return n.includes(current.name) ? n : `${current.name} ${n}`;
}

/** A product's subdomain ("tasks"); falls back to its id. */
export function productHost(id: string): string {
  return current.products[id]?.host ?? id;
}

/** One of the company's addresses at the public domain ("support@dugzi.com"). */
export function companyAddress(kind: keyof BrandConfig['addresses']): string {
  return `${current.addresses[kind]}@${current.domains.public}`;
}
